import { PrivilegeEnum } from '@/api/ApiClient';
import type { Operation, PrivilegeWithOperation } from '@/stores/authStore';
import type { EntityConfig } from '@/types/customizing';

/** Primary and alternate privileges that grant access to a customizing entity. */
export function getEntityPrivileges(config: EntityConfig): PrivilegeEnum[] {
  return [config.privilege, ...(config.alternatePrivileges ?? [])];
}

/** Operations that reveal the entity in nav / sidebar (any-of). */
export function getEntityVisibilityOperations(config: EntityConfig): Operation[] {
  if (config.visibilityOperations?.length) {
    return [...config.visibilityOperations];
  }
  return [config.visibilityOperation ?? 'read'];
}

/**
 * Privilege/operation pairs for route-config nav filtering.
 * Matches {@link canShowEntityInSidebar}: any privilege × any visibility operation.
 */
export function getEntityNavPrivileges(config: EntityConfig): PrivilegeWithOperation[] {
  const ops = getEntityVisibilityOperations(config);
  return getEntityPrivileges(config).flatMap((p) => ops.map((op) => ({ p, op })));
}

/** True when the user has `op` on any privilege wired to the entity. */
export function isEntityAuthorized(
  config: EntityConfig,
  isAuthorized: (privilege: PrivilegeEnum, op: Operation) => boolean,
  op: Operation
): boolean {
  return getEntityPrivileges(config).some((privilege) => isAuthorized(privilege, op));
}

/**
 * Sidebar / route visibility. Defaults to Read (Knockout CustomizingTab parity).
 * Use `visibilityOperations` when any of several ops should reveal the entity (e.g. Api Keys with Create-only).
 */
export function canShowEntityInSidebar(
  config: EntityConfig,
  isAuthorized: (privilege: PrivilegeEnum, op: Operation) => boolean
): boolean {
  return getEntityVisibilityOperations(config).some((op) => isEntityAuthorized(config, isAuthorized, op));
}

/**
 * Grid load / page access. Defaults to Read; most customizing list APIs use Read (HasPrivilege default).
 * Exceptions (e.g. users customizing list) set `gridAccessOperation: 'list'`.
 * When only mutation ops gate the entity (e.g. InstrumentFunding create/delete), fall back to
 * `visibilityOperations` so the grid can load for those users.
 */
export function canLoadEntityGrid(
  config: EntityConfig,
  isAuthorized: (privilege: PrivilegeEnum, op: Operation) => boolean
): boolean {
  const op = config.gridAccessOperation ?? config.visibilityOperation ?? 'read';
  if (isEntityAuthorized(config, isAuthorized, op)) {
    return true;
  }
  if (config.visibilityOperations?.length) {
    return config.visibilityOperations.some((v) => isEntityAuthorized(config, isAuthorized, v));
  }
  return false;
}

/** Whether customizing user load may include external (community) users. Requires User.List. */
export function canLoadExternalUsers(isAuthorized: (privilege: PrivilegeEnum, op: Operation) => boolean): boolean {
  return isAuthorized(PrivilegeEnum.User, 'list');
}

/** Whether customizing user load may include in-house staff. Requires User.List or ResearchGroup.List. */
export function canLoadInhouseUsers(isAuthorized: (privilege: PrivilegeEnum, op: Operation) => boolean): boolean {
  return isAuthorized(PrivilegeEnum.User, 'list') || isAuthorized(PrivilegeEnum.ResearchGroup, 'list');
}
